import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { LEGAL, LEGAL_ORDER } from "@/data/legal";
import { IMAGES } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];

export const Legal = () => {
  const { slug } = useParams();
  const doc = LEGAL[slug];
  if (!doc) return <Navigate to="/legal/privacy" replace />;

  return (
    <div className="relative min-h-screen w-full bg-[#040404] py-20" data-testid={`legal-${slug}`}>
      <div className="pointer-events-none absolute left-1/2 top-0 h-[46vh] w-[60vh] -translate-x-1/2 rounded-full bg-[#3B2A48]/12 blur-[130px]" />

      <div className="relative mx-auto max-w-3xl px-6 md:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-[#C5A059]"
          data-testid="legal-back-link"
        >
          <ArrowLeft size={15} strokeWidth={1.5} /> Return to the Veil
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="mt-12 flex flex-col items-start gap-6 border-b border-[#C5A059]/15 pb-10"
        >
          <img src={IMAGES.logo} alt="Sybils Society" className="h-16 w-auto object-contain" />
          <div>
            <p className="overline mb-4">Sybils Society — The Return of the Matriarchs</p>
            <h1 className="font-serif text-[clamp(2.4rem,6vw,4rem)] font-light leading-[1.02] tracking-tight text-white">
              {doc.title}
            </h1>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-white/35">
              {doc.updated}
            </p>
          </div>
        </motion.header>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="mt-10 font-serif text-xl font-light italic leading-relaxed text-white/70 md:text-2xl"
        >
          {doc.intro}
        </motion.p>

        <div className="mt-14 space-y-12">
          {doc.sections.map((s, i) => (
            <motion.section
              key={s.h}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-12%" }}
              transition={{ duration: 0.7, delay: i * 0.04, ease }}
              className="border-l border-[#C5A059]/20 pl-6 md:pl-8"
            >
              <h2 className="font-serif text-2xl font-light tracking-tight text-[#C5A059] md:text-3xl">
                {s.h}
              </h2>
              <p className="mt-4 text-base font-light leading-relaxed text-white/60">
                {s.p}
              </p>
            </motion.section>
          ))}
        </div>

        {/* Sibling document links */}
        <div className="mt-20 flex flex-wrap gap-4 border-t border-[#C5A059]/10 pt-10">
          {LEGAL_ORDER.filter((k) => k !== slug).map((k) => (
            <Link
              key={k}
              to={`/legal/${k}`}
              className="rounded-full border border-[#C5A059]/30 px-6 py-3 text-xs font-light uppercase tracking-[0.22em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505]"
              data-testid={`legal-link-${k}`}
            >
              {LEGAL[k].title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Legal;
